import {
	AfterViewInit,
	Component,
	ElementRef,
	OnInit,
	ViewChild,
	ViewEncapsulation,
	inject,
	NgZone,
	ChangeDetectionStrategy,
} from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { ProtocolContent } from "../../../../interfaces/protocols/protocol-content";
import { MenuFabItem } from "../../../../interfaces/menu-fab-item";
import { NotificationService } from "../../../../services/notification.service";
import { ErrorReportComponent } from "../../error-report/error-report.component";
import { UserService } from "../../../../services/user.service";
import { Stack } from "../../../../structures/stack";
import { ProtocolContentDraft } from "../../../../interfaces/protocols/protocol-content-draft";
import { ProtocolShareByEmailComponent } from "../../share/protocol-share-by-email/protocol-share-by-email.component";
import { DrugsService } from "../../../../services/drugs.service";
import { ProtocolAttachmentsListComponent } from "../../attachments/protocol-attachments-list/protocol-attachments-list.component";
import { VidalRecosService } from "../../../knowledge/vidal/vidal-recos/vidal-recos.service";
import { VideosService } from "../../../videos/videos.service";
import { ToolViewerService } from "../../../../services/tool-viewer.service";
import { ProtocolService } from "../../../../services/protocol.service";
import { finalize, first } from "rxjs";
import { DocumentViewerService } from "../../../../services/document-viewer.service";
import { MenuFABComponent } from "../../../core/menu-fab/menu-fab.component";
import { MatIcon } from "@angular/material/icon";
import { MatBadge } from "@angular/material/badge";
import { MatTooltip } from "@angular/material/tooltip";
import { MatButton } from "@angular/material/button";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";

import { TranslatePipe, TranslateService } from "@tolgee/ngx";
import { AdminService } from "../../../../services/admin.service";
import { MitpManagerService } from "../../../../services/mitp-manager.service";
import { ProtocolNote } from "../../notes/entities/protocol-note";
import { isTextQuoteAnchor } from "../../notes/entities/protocol-note-anchor";
import { ProtocolAnchorService } from "../../notes/protocol-anchor.service";
import { ProtocolNoteService } from "../../notes/protocol-note.service";
import {
	ProtocolNoteDialogComponent,
	ProtocolNoteDialogData,
} from "../../notes/protocol-note-dialog/protocol-note-dialog.component";
import {
	ProtocolNotesListComponent,
	ProtocolNotesListData,
	ProtocolNotesListResult,
} from "../../notes/protocol-notes-list/protocol-notes-list.component";

@Component({
	selector: "app-protocol-viewer-iframe",
	templateUrl: "./protocol-viewer-iframe.component.html",
	styleUrls: ["./protocol-viewer-iframe.component.scss"],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [MatButton, MatIcon, MatBadge, MatTooltip, MenuFABComponent, TranslatePipe],
})
export class ProtocolViewerIframeComponent implements OnInit, AfterViewInit {
	data = inject<ProtocolContent>(MAT_DIALOG_DATA);
	userService = inject(UserService);
	private dialog = inject(MatDialog);
	private toastr = inject(NotificationService);
	private translate = inject(TranslateService);
	private drugsService = inject(DrugsService);
	private vidalRecos = inject(VidalRecosService);
	private videosService = inject(VideosService);
	private toolViewerService = inject(ToolViewerService);
	private protocolService = inject(ProtocolService);
	private documentViewer = inject(DocumentViewerService);
	private adminService = inject(AdminService);
	private manager = inject(MitpManagerService);
	private sanitizer = inject(DomSanitizer);
	private zone = inject(NgZone);
	private noteService = inject(ProtocolNoteService);
	private anchorService = inject(ProtocolAnchorService);

	currentProtocolId: number;

	currentIsPublished: boolean;

	fabItems: MenuFabItem[] = [];
	navigationStack: Stack<number> = new Stack<number>();

	safeContent: SafeHtml;
	isLoading = false;

	notes: ProtocolNote[] = [];

	@ViewChild("iframeElement") iframeElement: ElementRef<HTMLIFrameElement>;

	constructor() {
		this.currentProtocolId = this.data.protocol.id;
	}

	ngOnInit(): void {
		this.currentIsPublished = this.data.isPublished;
		this.setFabMenu();
		if (this.data.content === null) {
			this.isLoading = true;
			if (this.data.isPublished) {
				this.protocolService
					.getPublishedProtocol(this.data.protocol.id)
					.pipe(first())
					.subscribe((content) => {
						this.loadProtocolContent(content, true);
					});
			} else {
				this.protocolService
					.getDraftProtocol(this.data.protocol.id)
					.pipe(first())
					.subscribe((draft) => {
						this.loadProtocolContent(this.formatBody(draft), false);
					});
			}
		} else {
			this.loadProtocolContent(this.data.content, this.data.isPublished);
		}
	}

	ngAfterViewInit() {
		// Listen to messages from the iframe (if we used postMessage)
		// OR simpler: Attach load event listener to iframe to inject scripts/check drugs
		// Since we use srcdoc, the 'load' event fires when the srcdoc is loaded.

		this.iframeElement.nativeElement.addEventListener("load", () => {
			this.onIframeLoad();
		});
	}

	onIframeLoad() {
		const doc = this.iframeElement.nativeElement.contentDocument;
		if (!doc) return;

		if (doc.getElementById("protocolContainer")) {
			this.zone.run(() => {
				this.isLoading = false;
			});
		}

		// Add Click Listener for Navigation and Actions
		doc.addEventListener("click", (e: any) => {
			this.zone.run(() => {
				const element = e.target || e.srcElement;

				// Le marqueur contient un SVG : la cible du clic n'est pas
				// forcément le marqueur lui-même, ni même un HTMLElement.
				const noteHolder = (element as Element)?.closest?.(`[${ProtocolAnchorService.NOTE_ID_ATTRIBUTE}]`);
				if (noteHolder) {
					e.preventDefault();
					this.openNotes(Number(noteHolder.getAttribute(ProtocolAnchorService.NOTE_ID_ATTRIBUTE)));
					return;
				}

				if (element instanceof (doc.defaultView as any).HTMLElement) {
					if (element.tagName === "A") {
						const link = element.getAttribute("href");
						if (link == null) {
							return;
						}
						e.preventDefault(); // Stop default navigation
						this.handleLink(link);
					} else if (element.tagName === "MITP-DRUG") {
						e.preventDefault();
						this.drugsService.showDrug(element.getAttribute("reference"));
					}
				}
			});
		});

		// Initialize TOC inside the iframe
		// We injected the script tag in generateHtml, so initTOC should be available on the window
		// But we need to call it.
		const win = this.iframeElement.nativeElement.contentWindow as any;
		if (win && win.initTOC) {
			this.initTOCInIframe(win, doc);
		} else {
			// Retry if script not yet parsed (though load event should imply it is)
			// Or if we need to wait for the script to load
		}

		// Check Drugs
		this.checkDrugs(doc);

		this.loadNotes();
	}

	initTOCInIframe(win: any, doc: Document) {
		// Create TOC container inside the iframe if it doesn't exist or we want to place it
		// In the old component, #toc was separate. Here it needs to be inside or we pull it out?
		// The old component had <div #toc> above the protocol body.
		// So our generated HTML should include a #toc div.

		const tocContainer = doc.getElementById("toc");
		if (!tocContainer) return;

		// Clear existing
		while (tocContainer.childNodes.length > 0) {
			tocContainer.removeChild(tocContainer.childNodes[0]);
		}

		const toc = win.initTOC({
			selector: "h1, h2, h3, h4, h5, h6",
			scope: "#protocolContainer",
			overwrite: false,
			prefix: "mitp-toc",
		});

		if (toc && toc.childNodes.length > 0) {
			tocContainer.appendChild(toc);
			const tocItems = doc.querySelectorAll('*[id^="tocheader-"]');
			tocItems.forEach((item: any) => {
				const id = item.id.substr("tocheader-".length);
				item.addEventListener("click", () => {
					const el = doc.getElementById(id);
					if (el) el.scrollIntoView();
				});
			});

			// Show title if TOC exists
			const tocTitle = doc.getElementById("tocTitle");
			if (tocTitle) tocTitle.style.display = "block";
		}
	}

	handleLink(link: string) {
		if (link.startsWith("mitp://")) {
			const schema = link.substring("mitp://".length, link.length);
			const data = schema.split("/");
			let id: number;
			switch (data[0]) {
				case "protocols":
					id = +data[1];
					if (id) {
						this.navigationStack.push(this.currentProtocolId);
						this.loadProtocolById(id);
					}
					break;
				case "videos":
					id = +data[1];
					if (id) {
						this.videosService.playVideo(id);
					}
					break;
				case "attachments":
					id = +data[1];
					if (id) {
						this.protocolService
							.downloadProtocolAttachment(this.currentProtocolId, id)
							.pipe(first())
							.subscribe((response) => {
								this.documentViewer.viewDocument(new Uint8Array(response.body));
							});
					}
					break;
				case "vidal":
					if (data[1] === "recos") {
						this.navigationStack.push(this.currentProtocolId);
						this.loadVidalRecos(+data[2]);
					}
					break;
				case "scores":
					id = +data[1];
					if (id) {
						this.toolViewerService.openScore(id);
					}
					break;
				case "formulas":
					id = +data[1];
					if (id) {
						this.toolViewerService.openFormula(id);
					}
					break;
				case "scales":
					id = +data[1];
					if (id) {
						this.toolViewerService.openScale(id);
					}
					break;
				case "tools":
					id = +data[1];
					if (id) {
						this.toolViewerService.openMedicalTool(id);
					}
					break;
			}
		} else if (link.startsWith("http://")) {
			this.toastr.error(this.translate.instant("protocol.viewer.error.http"));
		} else if (link.startsWith("https://")) {
			window.open(link, "_blank");
		}
	}

	loadProtocolContent(fullContent: string, isPublished: boolean) {
		// We need to parse 'fullContent' to separate header/body if they are separated by comments,
		// or just pass it all if the format allows.
		// Old component: [this.header, this.body] = this.data.content.split("<!-- Protocol Body -->");

		let header = "";
		let body = fullContent;
		if (fullContent.includes("<!-- Protocol Body -->")) {
			[header, body] = fullContent.split("<!-- Protocol Body -->");
		}

		const doc = this.iframeElement?.nativeElement?.contentDocument;
		const hasShell = doc && doc.getElementById("protocolContainer");
		// const isVidal = false; // We can track this if needed, but for now assuming typical protocol flow

		// If we have a shell, and the publication status hasn't changed (requiring different scripts), update in-place
		if (hasShell && this.currentIsPublished === isPublished) {
			this.updateIframeContent(doc, header, body);
		} else {
			this.currentIsPublished = isPublished;
			this.generateHtml(header, body, isPublished);
		}
	}

	updateIframeContent(doc: Document, header: string, body: string) {
		// Update Content
		const headerEl = doc.getElementById("header");
		if (headerEl) headerEl.innerHTML = header;

		const bodyEl = doc.getElementById("protocolContainer");
		if (bodyEl) bodyEl.innerHTML = body;

		// Update Watermark (though unlikely to change within same session unless data.protocol changes? actually data.protocol is static for the viewer instance)
		// But if we navigated to a protocol from another institute? The viewer is usually for ONE protocol context?
		// Navigation stack suggests we can go to OTHER protocols.
		// We assume 'this.data.protocol' refers to the ROOT protocol opened.
		// If we navigate to another protocol, we might need to check its institute?
		// But 'loadProtocolById' only gets content, not the full Protocol object with Institute info.
		// So we can't easily update the watermark without fetching metadata.
		// For now, we leave the watermark of the original protocol.

		// Re-run TOC
		const win = this.iframeElement.nativeElement.contentWindow as any;
		if (win && win.initTOC) {
			this.initTOCInIframe(win, doc);
		}

		// Re-run Check Drugs
		this.checkDrugs(doc);

		// innerHTML a été remplacé : les surlignages posés précédemment ont disparu.
		this.loadNotes();
	}

	loadProtocolById(id: number, publishedVersion = true) {
		this.currentProtocolId = id;
		if (publishedVersion) {
			this.protocolService.getPublishedProtocol(id).subscribe((content) => {
				this.loadProtocolContent(content, true);
			});
		} else {
			this.protocolService.getDraftProtocol(id).subscribe((content) => {
				const formatted = this.formatBody(content);
				this.loadProtocolContent(formatted, false);
			});
		}
	}

	loadVidalRecos(id: number) {
		this.vidalRecos.getById(id + "").subscribe((response) => {
			this.generateHtml("", response, false, true); // Treat as special content
		});
	}

	formatBody(content: ProtocolContentDraft): string {
		return `<div style="background-color:#005db6;color:white;text-align:center;padding:16px;font-size:25px">Version Brouillon - <i>Non Publiée</i></div><!-- Protocol Body -->${content.content}<script id="mitp-script">${content.code}</script>`;
	}

	generateHtml(header: string, body: string, isPublished: boolean, isVidal = false) {
		// Fetch external JS/CSS if needed
		let cssPromise = Promise.resolve("");
		let jsPromise = Promise.resolve("");

		if (!isPublished && !isVidal) {
			cssPromise = new Promise((resolve) => this.adminService.getProtocolCss().subscribe(resolve));
			jsPromise = new Promise((resolve) => this.adminService.getProtocolJs().subscribe(resolve));
		}

		Promise.all([cssPromise, jsPromise]).then(([extraCss, extraJs]) => {
			// Extract embedded JS from body if any (<script id="mitp-script">)
			// In string manipulation it's messy.
			// The browser will parse it in the iframe.
			// However, the old component extracts it and re-injects it to ensure execution.
			// srcdoc execution rules: scripts run.

			// Construct HTML
			// Note: We include <base href> to ensure relative assets work.

			const watermark =
				this.data.protocol.institute.id !== this.userService.getInstituteId()
					? `<div id="background" style="position:fixed;z-index:0;left:50%;top:50%;opacity:0.2;color:#000;font-size:24px;font-weight:700;transform:translate(-50%,-50%) rotate(-45deg)">
                    ${this.translate.instant("PROTOCOL-VIEWER_INDICATION_APPLY")} ${this.data.protocol.institute.label}
                   </div>`
					: "";

			const html = `
                <!DOCTYPE html>
                <html>
                <head>
                    <base href="${document.baseURI}">
                    <meta charset="utf-8">
                    <script src="https://cdn.tailwindcss.com"></script>
                    <script>
                        tailwind.config = {
                            darkMode: 'class',
                            corePlugins: {
                                preflight: false
                            }
                        }
                    </script>
                    <script src="assets/js/toc.min.js"></script>
                    <link rel="stylesheet" href="https://api.medinthepocket.com/public/resources/protocols/css">
                    <link rel="stylesheet" href="assets/css/version-compare.css">
                    <style>
                        html { color-scheme: light; }
                        body { font-family: Roboto, "Helvetica Neue", sans-serif; margin: 16px; background-color: white; color: black; }
                        /* Une image plus large que le cadre imposait un défilement horizontal : on la borne au cadre, sans jamais l'agrandir. */
                        img { max-width: 100%; height: auto; }
                        /* Add SafeHtml pipe styles equivalents if any */
                        mitp-drug.shortage { background-color: #ffcccc; border: 1px solid red; }
                        mark.mitp-note-highlight { background-color: #fff3bf; border-radius: 2px; padding: 0 1px; color: inherit; cursor: pointer; }
                        mark.mitp-note-highlight-focus { box-shadow: 0 0 0 2px #005db6; }
                        .mitp-note-marker { display: inline-flex; align-items: center; justify-content: center; width: 18px; height: 18px; margin-left: 2px; border-radius: 50%; background-color: #005db6; color: #fff; cursor: pointer; vertical-align: super; }
                        .mitp-note-marker svg { display: block; }
                        @media print { .mitp-note-marker { display: none; } mark.mitp-note-highlight { background-color: transparent; } }
                        ${extraCss}
                    </style>
                </head>
                <body>
                    <div id="protocolView">
                        ${watermark}
                        <div id="header">${header}</div>
                        <p></p>
                        <p id="tocTitle" style="display:none">${this.translate.instant("protocol.viewer.toc.title")}</p>
                        <div id="toc"></div>
                        <p></p>
                        <div id="protocolContainer">${body}</div>
                    </div>
                    <script>
                        ${extraJs}
                    </script>
                </body>
                </html>
             `;

			this.safeContent = this.sanitizer.bypassSecurityTrustHtml(html);
		});
	}

	checkDrugs(doc: Document): void {
		const ucds: string[] = [];
		doc.querySelectorAll("mitp-drug").forEach((element: any) => {
			ucds.push(element.getAttribute("reference"));
		});
		if (ucds.length == 0) {
			return;
		}
		this.drugsService
			.checkShortage(ucds)
			.pipe(first())
			.subscribe((shortages) => {
				shortages.forEach((ucd) => {
					doc.querySelector(`mitp-drug[reference="${ucd}"]`)?.classList.add("shortage");
				});
			});
	}

	back(): void {
		if (this.navigationStack.size() > 1) {
			const id = this.navigationStack.pop();
			this.loadProtocolById(id);
		} else if (this.navigationStack.size() == 1) {
			const id = this.navigationStack.pop();
			this.loadProtocolById(id, this.data.isPublished);
		}
	}

	// Static print helper now needs to print the iframe content
	printProtocol() {
		const iframeWindow = this.iframeElement?.nativeElement?.contentWindow;
		if (!iframeWindow) {
			return;
		}
		iframeWindow.focus();
		iframeWindow.print();
	}

	fabMenuCallback(fnLabel: string) {
		switch (fnLabel) {
			case "error_report": {
				// Selection might be inside iframe
				const win = this.iframeElement.nativeElement.contentWindow;
				const error = win ? win.getSelection()?.toString() : "";

				if (!error) {
					this.toastr.warning(this.translate.instant("protocol.error.warning.select"));
					return;
				}
				this.dialog
					.open(ErrorReportComponent, {
						data: { protocol: this.data.protocol, error: error },
						minWidth: "600px",
						disableClose: true,
					})
					.afterClosed()
					.subscribe((answer) => {
						if (!answer) {
							this.toastr.info(this.translate.instant("protocol.error.report.cancel"));
						}
					});
				return;
			}
			case "new_note":
				this.createNote();
				return;
			case "print":
				this.printProtocol();
				return;
			case "favorite":
				if (this.currentProtocolId <= 0) {
					return;
				}
				this.userService
					.addProtocolToFavorites(this.currentProtocolId)
					.pipe(first())
					.subscribe({
						next: () => {
							this.toastr.success(this.translate.instant("core.message.success"));
						},
						error: () => {
							this.toastr.error(this.translate.instant("core.message.error.unknown"));
						},
					});
				return;
			case "share":
				this.dialog.open(ProtocolShareByEmailComponent, {
					data: this.currentProtocolId,
					width: "600px",
					disableClose: true,
				});
				return;
			case "attachments":
				this.dialog.open(ProtocolAttachmentsListComponent, {
					data: this.currentProtocolId,
					width: "600px",
					disableClose: true,
					autoFocus: false,
				});
				return;
			case "translate":
				this.manager.showLoading(true);
				this.protocolService
					.translate(this.currentProtocolId)
					.pipe(
						first(),
						finalize(() => this.manager.showLoading(false))
					)
					.subscribe((translation) => {
						console.log(translation);
					});
				return;
		}
	}

	/**
	 * Les notes sont rattachées à une version publiée : un brouillon n'en a
	 * aucune, et le contenu Vidal n'est pas un protocole.
	 */
	get canUseNotes(): boolean {
		return this.currentIsPublished && this.currentProtocolId > 0;
	}

	/** Ouvre la liste, éventuellement centrée sur la note dont on a cliqué le marqueur. */
	openNotes(focusNoteId?: number): void {
		if (!this.canUseNotes) {
			return;
		}

		this.dialog
			.open(ProtocolNotesListComponent, {
				data: { protocolId: this.currentProtocolId, focusNoteId: focusNoteId } as ProtocolNotesListData,
				width: "700px",
				maxHeight: "80vh",
				autoFocus: false,
			})
			.afterClosed()
			.pipe(first())
			.subscribe((result: ProtocolNotesListResult | undefined) => {
				if (!result) {
					return;
				}
				const locate = result.action === "locate" && result.note != null;
				if (result.changed) {
					// Le rendu doit être refait avant de pouvoir viser un surlignage.
					this.loadNotes(locate ? () => this.scrollToNote(result.note.id) : undefined);
				} else if (locate) {
					this.scrollToNote(result.note.id);
				}
			});
	}

	private createNote(): void {
		if (!this.canUseNotes) {
			return;
		}

		const container = this.getProtocolContainer();
		const win = this.iframeElement?.nativeElement?.contentWindow;
		if (!container || !win) {
			return;
		}

		// La sélection est facultative : une note peut porter sur tout le protocole.
		const selection = this.anchorService.buildAnchorFromSelection(container, win);

		this.dialog
			.open(ProtocolNoteDialogComponent, {
				data: {
					protocolId: this.currentProtocolId,
					selectedText: selection.selectedText,
					anchor: selection.anchor,
				} as ProtocolNoteDialogData,
				width: "600px",
				disableClose: true,
			})
			.afterClosed()
			.pipe(first())
			.subscribe((note) => {
				if (note) {
					this.loadNotes();
				}
			});
	}

	private loadNotes(onRendered?: () => void): void {
		if (!this.canUseNotes) {
			this.notes = [];
			return;
		}

		const protocolId = this.currentProtocolId;
		this.noteService
			.getProtocolNotes(protocolId)
			.pipe(first())
			.subscribe({
				next: (notes) => {
					// Une navigation mitp:// a pu changer de protocole entre-temps.
					if (protocolId !== this.currentProtocolId) {
						return;
					}
					this.zone.run(() => {
						this.notes = notes ?? [];
					});
					this.renderNotes();
					onRendered?.();
				},
				error: () => undefined,
			});
	}

	/** Repose surlignages et marqueurs sur le contenu actuellement rendu. */
	private renderNotes(): void {
		const container = this.getProtocolContainer();
		if (!container) {
			return;
		}

		this.anchorService.clearHighlights(container);
		this.notes
			// Une ancre écrite sur une version antérieure ne désigne plus le même
			// passage : la retrouver par coïncidence serait pire que ne rien montrer.
			.filter((note) => !note.outdated && isTextQuoteAnchor(note.selection_anchor))
			.forEach((note) => this.anchorService.highlight(container, note.selection_anchor, note.id));
	}

	private scrollToNote(noteId: number): void {
		const container = this.getProtocolContainer();
		const highlight = container ? this.anchorService.findHighlight(container, noteId) : null;
		if (!highlight) {
			this.toastr.info(this.translate.instant("protocols.notes.locate.missing"));
			return;
		}

		highlight.scrollIntoView({ behavior: "smooth", block: "center" });
		highlight.classList.add("mitp-note-highlight-focus");
		this.zone.runOutsideAngular(() =>
			setTimeout(() => highlight.classList.remove("mitp-note-highlight-focus"), 2000)
		);
	}

	private getProtocolContainer(): HTMLElement | null {
		return this.iframeElement?.nativeElement?.contentDocument?.getElementById("protocolContainer") ?? null;
	}

	private setFabMenu() {
		const itemPrint: MenuFabItem = {
			label: "print",
			tooltip: this.translate.instant("protocol.print.button"),
			icon: "print",
		};
		const itemErrorReport: MenuFabItem = {
			icon: "error",
			label: "error_report",
			tooltip: this.translate.instant("protocol.error.report.button"),
		};
		this.fabItems.push(itemPrint);
		if (this.currentProtocolId > 0) {
			this.fabItems.push({
				label: "favorite",
				tooltip: this.translate.instant("protocol.viewer.favorite.tooltip"),
				icon: "star",
			});
		}
		this.fabItems.push(itemErrorReport);
		this.fabItems.push({
			label: "share",
			tooltip: this.translate.instant("protocol.viewer.share.tooltip"),
			icon: "email",
		});
		this.fabItems.push({
			label: "attachments",
			tooltip: this.translate.instant("protocol.viewer.attachments.tooltip"),
			icon: "attach_file",
		});
		if (this.canUseNotes) {
			this.fabItems.push({
				label: "new_note",
				tooltip: this.translate.instant("protocol.viewer.new_note.tooltip"),
				icon: "sticky_note_2",
			});
		}
	}
}
